'use client'

/**
 * Account state in the page header.
 *
 * Signed out it is two links. Signed in it is an avatar button that opens a
 * dropdown — Dashboard, Profile, Admin (administrators only), Sign out.
 *
 * ── Two contexts, one component ────────────────────────────────────────────
 *
 * The admin console shows the same control in its top-right corner, and it is
 * this component rather than a second one: the three dismissal handlers below
 * and the server-side sign-out are the parts that are easy to get subtly wrong,
 * and a copy would have to get them right twice. Only the LINKS differ, because
 * the useful destinations do — "Admin console" is noise when you are already
 * inside it, and "Back to search" is the exit the console needs.
 *
 * Signing out is a POST to the API so the session is destroyed server-side —
 * clearing the cookie in the browser alone would leave a valid session behind
 * (§62.4). `router.refresh()` afterwards re-runs the server components so the
 * header, and any page that reads the session, reflect the change immediately.
 *
 * ── The dropdown, and why it is hand-rolled ────────────────────────────────
 *
 * No dependency: a menu is a button, a list, and three event handlers, and this
 * project adds infrastructure only for a demonstrated need (§38). The three
 * handlers are the ones that are always forgotten, so they are all here:
 *
 *   · **Escape** closes and returns focus to the button.
 *   · **Click outside** closes — listened on `pointerdown`, not `click`, so a
 *     press that starts outside dismisses before the target's own click fires.
 *   · **Focus leaving the menu** closes, which is what makes Tab out of the last
 *     item behave like every native menu.
 *
 * `aria-expanded` and `aria-haspopup` are on the trigger, and the panel is a
 * `role="menu"` of `role="menuitem"`s, so a screen reader announces it as a menu
 * rather than as a stack of links that appeared from nowhere.
 */
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useId, useRef, useState } from 'react'
import { UserAvatar } from './UserAvatar'

export type HeaderUser = {
  username: string
  role: 'user' | 'admin'
  /** Uploaded picture, from `avatarUrl(user)` — null falls back to the initial. */
  avatarSrc: string | null
  /**
   * Shown in the menu head where WHICH account is signed in matters — the
   * console, where the actions are destructive and shared logins are common.
   * Omitted on the public header, which already names the account.
   */
  email?: string
} | null

/** One 20×20 stroked glyph path, shared by the two link sets below. */
const GLYPH = {
  dashboard: 'M3 3h6v6H3zM11 3h6v4h-6zM11 9h6v8h-6zM3 11h6v6H3z',
  profile: 'M10 3a3.5 3.5 0 110 7 3.5 3.5 0 010-7zM3.5 17c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5',
  console: 'M10 2.5l6 2.5v5c0 3.6-2.5 6.6-6 7.5-3.5-.9-6-3.9-6-7.5V5z',
  search: 'M9 3.5a5.5 5.5 0 105.5 5.5A5.5 5.5 0 009 3.5zM13.1 13.1l3.4 3.4',
  signOut: 'M12 6V4H4v12h8v-2M9 10h8m0 0l-2.5-2.5M17 10l-2.5 2.5',
}

/** Where this menu can take you, which depends on where it is being shown. */
function destinations(role: 'user' | 'admin', context: 'site' | 'admin') {
  if (context === 'admin') {
    return [
      { href: '/', label: 'Back to search', icon: GLYPH.search },
      { href: '/dashboard', label: 'My dashboard', icon: GLYPH.dashboard },
      { href: '/dashboard/profile', label: 'Profile', icon: GLYPH.profile },
    ]
  }
  return [
    { href: '/dashboard', label: 'Dashboard', icon: GLYPH.dashboard },
    { href: '/dashboard/profile', label: 'Profile', icon: GLYPH.profile },
    ...(role === 'admin'
      ? [{ href: '/admin', label: 'Admin console', icon: GLYPH.console }]
      : []),
  ]
}

export function AccountMenu({
  user,
  context = 'site',
}: {
  user: HeaderUser
  /** `admin` renders the console's link set — see the note at the top. */
  context?: 'site' | 'admin'
}) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onFocusIn = (e: FocusEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [open])

  if (!user) {
    return (
      <div className="account">
        <Link href="/signin" className="account-link">
          Sign in
        </Link>
        <Link href="/signup" className="btn btn-primary btn-sm">
          Sign up
        </Link>
      </div>
    )
  }

  async function signOut() {
    setBusy(true)
    try {
      await fetch('/api/auth/logout', { method: 'POST' })
      router.refresh()
      router.push('/')
    } finally {
      setBusy(false)
      setOpen(false)
    }
  }

  return (
    <div className="account account-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="account-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
      >
        <UserAvatar username={user.username} size="sm" src={user.avatarSrc} />
        <span className="account-trigger-name">@{user.username}</span>
        {user.role === 'admin' && <AdminBadge />}
        <svg className="account-caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div className="menu" id={menuId} role="menu">
          <div className="menu-head">
            <UserAvatar username={user.username} size="md" src={user.avatarSrc} />
            <div className="menu-head-text">
              <b>@{user.username}</b>
              <span>{user.role === 'admin' ? 'Administrator' : 'Member'}</span>
              {user.email && <span className="menu-head-mail">{user.email}</span>}
            </div>
          </div>

          {destinations(user.role, context).map((item) => (
            <Link
              key={item.href}
              href={item.href}
              role="menuitem"
              className="menu-item"
              onClick={() => setOpen(false)}
            >
              <MenuIcon d={item.icon} />
              {item.label}
            </Link>
          ))}

          <div className="menu-sep" role="separator" />

          <button type="button" role="menuitem" className="menu-item" onClick={signOut} disabled={busy}>
            <MenuIcon d={GLYPH.signOut} />
            {busy ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * The administrator mark on the account card.
 *
 * Real text, not a coloured dot: `Admin` is announced to a screen reader as part
 * of the trigger's accessible name, so the one piece of information that changes
 * what this session is allowed to do is not carried by colour alone.
 *
 * Filled gold — `--brand` over `--on-brand`, the same pairing as a primary
 * button — because this is the only badge on the chrome and a quiet one would
 * defeat the purpose. It renders wherever the menu does, the console included:
 * "you are signed in as an administrator" is worth restating precisely on the
 * pages where that is true and consequential.
 *
 * The word alone. It used to carry the shield glyph the "Admin console" link
 * uses, and inside a filled capsule five characters wide that mark was pure
 * ornament — it said nothing the word beside it did not, and the asymmetric
 * padding it needed made the pill look mis-set next to the caret.
 */
function AdminBadge() {
  return <span className="account-badge">Admin</span>
}

/** One 20×20 stroked glyph. Inline so the menu costs no icon dependency. */
function MenuIcon({ d }: { d: string }) {
  return (
    <svg className="menu-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
